"use client"

import { useEffect, useRef, useState } from "react"
import { Check, Copy } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useSharedUiTranslations } from "@/components/i18n/ui-control-labels-context"


/** Icon-only copy. The icon turns into a check for a moment instead of a text change, so the layout never shifts. */
export function CopyButton({ value, label, className }: { value: string; label: string; className?: string }) {
  const tUi = useSharedUiTranslations("CopyButton")

  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 2000)
      toast.success(`${label} copied`)
    } catch {
      toast.error(`Unable to copy the ${label.toLowerCase()}`)
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7 shrink-0", className)}
      onClick={copy}
      aria-label={tUi("copyValue0", { value0: String(label.toLowerCase()) })}
      title={tUi("copyValue0", { value0: String(label.toLowerCase()) })}
    >
      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
    </Button>
  )
}
